import { stringUpperCase } from "@src/components/utils/stringUtils";
import { TActiveInitialState } from "@src/store/marketplace/seatgeek/getChartActiveDataPlus";
import { TSoldInitialState } from "@src/store/marketplace/seatgeek/getChartSoldDataPlus";
import _ from "lodash";

type TActiveChart = {
	rawSections: Array<string>;
	data: TActiveInitialState;
};

type TSalesChart = {
	rawSections: Array<string>;
	data: TSoldInitialState;
};

export const useActiveChartData = ({ rawSections, data }: TActiveChart) => {
	const { eventData, sectionData } = data;
	if (!rawSections.length) {
		return eventData;
	}

	const filteredData = sectionData.filter((x) => {
		return rawSections.includes(stringUpperCase(normalizeSectionPlus(x.section)));
	});

	const groupedData = _.groupBy(filteredData, (x) => x.date);

	const aggregatedData = Object.entries(groupedData).map(([date, events]) => {
		const totalTicketCount = events.reduce((sum, event) => sum + event.listingsCount, 0);
		const totalGetInPrice = events.reduce((sum, event) => sum + event.getInPrice, 0);
		const totalWeightedPrice = events.reduce((sum, event) => sum + event.averagePrice * event.listingsCount, 0);
		const computedAveragePrice = totalTicketCount > 0 ? totalWeightedPrice / totalTicketCount : 0;
		const computedAverageGetInPrice = totalGetInPrice > 0 ? totalGetInPrice / totalTicketCount : 0;

		return {
			date,
			section: events[0].section,
			averagePrice: computedAveragePrice,
			listingsCount: totalTicketCount,
			getInPrice: computedAverageGetInPrice,
		};
	});
	return aggregatedData;
};

export const useSalesChartData = ({ rawSections, data }: TSalesChart) => {
	const { eventData, sectionData } = data;
	if (!rawSections.length) {
		return eventData;
	}

	const filteredData = sectionData.filter((x) => rawSections.includes(stringUpperCase(x.section)));
	const groupedData = _.groupBy(filteredData, (x) => x.date);

	const aggregatedData = Object.entries(groupedData).map(([date, events]) => {
		const totalTicketCount = events.reduce((sum, event) => sum + event.ticketCount, 0);
		const totalWeightedPrice = events.reduce((sum, event) => sum + event.averagePrice * event.ticketCount, 0);
		const computedAveragePrice = totalTicketCount > 0 ? totalWeightedPrice / totalTicketCount : 0;

		return {
			date,
			section: events[0].section,
			averagePrice: computedAveragePrice,
			ticketCount: totalTicketCount,
		};
	});

	return aggregatedData;
};

function normalizeSectionPlus(sectionStr: string): string {
	const trimmed = sectionStr.trim();
	const regex = /^(TW|T|F|WALL|M|CL|C)\s*(\d+)/i;
	const match = regex.exec(trimmed);
	if (!match) {
		return sectionStr;
	}
	const prefix = match[1]?.toUpperCase();
	const num = parseInt(match[2], 10);

	switch (prefix) {
		case "T":
			return `Terrace ${num - 2}`;
		case "TW":
			return `Terrace Wall ${num + 3}`;
		case "F":
			return `Floor ${num}`;
		case "WALL":
			return `Wall ${num - 4}`;
		case "M":
			return `Main ${num}`;
		case "CL":
		case "C":
			return `Club ${num}`;
		default:
			return sectionStr;
	}
}
